<template>
  <div class="page-container bg-white rounded-xl">
    <div class="container mx-auto">
      <div v-if="alertIsVisible" class="alert" :class="alertType">
        {{ alertMessage }}
        <button class="close-button" @click="closeAlert">&times;</button>
      </div>

      <div class="py-3 px-2 lg:py-6 lg:px-6 relative">
        <h3 class="text-xl lg:text-3xl font-bold text-[#00423D] capitalize pt-6 pb-6 flex justify-between items-center">
          <div>
            <span class="text-[#00423D]">Hello,</span>
            {{ userInformation.firstName }}
            {{ userInformation.lastName }}!
          </div>
          <button
            @click="scrollToSubscription"
            class="bg-[#00423D] text-white font-semibold text-[12px] md:text-[14px] leading-[16px] md:leading-[20px] rounded-[8px] px-3 md:px-4 py-2 hover:bg-[#00332f] transition-colors duration-200 flex items-center justify-center"
          >
            <span class="hidden md:inline">View Subscription</span>
            <span class="md:hidden">Subscription</span>
          </button>
        </h3>

        <div class="line-separate"></div>

        <!-- Personal Information Section -->
        <div class="flex flex-col py-5 my-10 lg:py-6 lg:px-6">
          <div>
            <p class="text-lg lg:text-2xl font-bold text-[#00423D] capitalize mb-5">
              Personal Information:
            </p>

            <div class="flex flex-wrap align-bottom items-center md:items-end lg:items-end mt-5">
              <div class="w-full md:w-4/5">
                <!-- Profile Image Upload -->
                <FormLogoUpload
                  v-model="personalForm.image"
                  label="Upload Profile Image"
                  required
                  :placeholder="profileImageDisplay"
                  :errorMessage="shouldShowError(v$.personalForm.image) ? v$.personalForm.image.$errors[0].$message : ''"
                />

                <div class="flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="personalForm.name"
                      label="Full Name"
                      type="text"
                      placeholder="Enter full name"
                      :errorMessage="shouldShowError(v$.personalForm.name) ? v$.personalForm.name.$errors[0].$message : ''"
                      required
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="personalForm.position"
                      label="Position"
                      type="text"
                      placeholder="Enter position"
                      :errorMessage="shouldShowError(v$.personalForm.position) ? v$.personalForm.position.$errors[0].$message : ''"
                      required
                    />
                  </div>
                </div>

                <div class="flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormEmailInput 
                      v-model="personalForm.email" 
                      :readonly="true"
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormPhoneInput
                      label="Phone Number"
                      v-model="personalForm.phone"
                      required
                      :defaultCountry="personalForm.phoneCountry"
                      :errorMessage="shouldShowError(v$.personalForm.phone) ? v$.personalForm.phone.$errors[0].$message : ''"
                      @validate="handlePersonalPhoneValidation"
                      @country-changed="handlePersonalCountryChange"
                      @blur="personalFieldTouched.phone = true"
                    />
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/5 flex justify-center md:justify-end mt-4 md:mt-0 px-2">
                <button
                  @click="updatePersonalInfo"
                  ref="personalSubmitBtn"
                  :class="`w-full h-[48px] md:h-[48px] bg-[#00423D] text-white font-semibold text-[12px] md:text-[16px] leading-[16px] md:leading-[24px] rounded-[12px] px-2 md:px-6 py-2 md:py-3 flex items-center justify-center`"
                  :disabled="isSendingPersonalRequest || !isPersonalFormChanged"
                >
                  <transition name="button-switch" mode="out-in">
                    <span v-if="!isSendingPersonalRequest">Update Profile</span>
                    <div v-else class="max-h-6 overflow-hidden inline-flex items-center justify-center pt-2">
                      <Vue3Lottie :animationData="buttonAnimation" :height="120" :width="120" />
                    </div>
                  </transition>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isOwner">
        <div class="line-separate"></div>

        <!-- Organization Information Section -->
        <div class="flex flex-col py-5 my-10 lg:py-6 lg:px-6">
          <div>
            <p class="text-lg lg:text-2xl font-bold text-[#00423D] capitalize">
              Organization Information:
            </p>

            <div class="flex flex-wrap align-bottom items-center md:items-end lg:items-end mt-5">
              <div class="w-full md:w-4/5">
                <!-- Logo Upload -->
                <FormLogoUpload 
                  v-model="form.logo" 
                  label="Upload Organization Logo" 
                  required 
                  :placeholder="logoDisplay" 
                  :errorMessage="shouldShowError(v$.form.logo) ? v$.form.logo.$errors[0].$message : ''" 
                />

                <div class="flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="form.name"
                      label="Organization Name"
                      type="text"
                      placeholder="Enter organization name"
                      :errorMessage="shouldShowError(v$.form.name) ? v$.form.name.$errors[0].$message : ''"
                      required
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="form.directorName"
                      label="Director Name"
                      type="text"
                      placeholder="Enter director name"
                      :errorMessage="shouldShowError(v$.form.directorName) ? v$.form.directorName.$errors[0].$message : ''"
                      required
                    />
                  </div>
                </div>

                <div class="mt-3 flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormPhoneInput 
                      label="Organization Phone Number" 
                      v-model="form.phone" 
                      required 
                      :defaultCountry="form.phoneCountry" 
                      :errorMessage="shouldShowError(v$.form.phone) ? v$.form.phone.$errors[0].$message : ''" 
                      @validate="handlePhoneValidation"
                      @country-changed="handleCountryChange"
                      @blur="fieldTouched.phone = true"
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="form.address"
                      label="Organization Address"
                      type="text"
                      placeholder="Enter address"
                      :errorMessage="shouldShowError(v$.form.address) ? v$.form.address.$errors[0].$message : ''"
                      required
                    />
                  </div>
                </div>

                <div class="mt-3 flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="form.website"
                      label="Organization Website"
                      type="text"
                      placeholder="Enter website"
                      :errorMessage="shouldShowError(v$.form.website) ? v$.form.website.$errors[0].$message : ''"
                      required
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="form.year"
                      label="Year"
                      type="text"
                      placeholder="YYYY"
                      :errorMessage="shouldShowError(v$.form.year) ? v$.form.year.$errors[0].$message : ''"
                      required
                      :maxlength="4"
                      @input="formatYear"
                    />
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/5 flex justify-center md:justify-end mt-4 md:mt-0 px-2">
                <button
                  @click="submitForm"
                  ref="submitBtn"
                  :class="`w-full h-[48px] md:h-[48px] bg-[#00423D] text-white font-semibold text-[12px] md:text-[16px] leading-[16px] md:leading-[24px] rounded-[12px] px-2 md:px-6 py-2 md:py-3 flex items-center justify-center`"
                  :disabled="isSendingRequest"
                >
                  <transition name="button-switch" mode="out-in">
                    <span v-if="!isSendingRequest">Save Changes</span>
                    <div v-else class="max-h-6 overflow-hidden inline-flex items-center justify-center pt-2">
                      <Vue3Lottie :animationData="buttonAnimation" :height="120" :width="120" />
                    </div>
                  </transition>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

        <div class="line-separate"></div>

        <!-- Subscription Details Section -->
        <div id="subscription-details" class="flex flex-col py-5 my-10 lg:py-6 lg:px-6">
          <div>
            <p class="text-lg lg:text-2xl font-bold text-[#00423D] capitalize mb-5">
              Subscription Details:
            </p>

            <div class="flex flex-wrap align-bottom items-center md:items-end lg:items-end mt-5">
              <div class="w-full md:w-4/5">
                <div v-if="subscriptionLoading" class="flex justify-center items-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00423D]"></div>
                </div>
                
                <div v-else-if="subscriptionDetails" class="space-y-4">
                  <!-- Plan with Term and Status Badge -->
                  <div class="flex items-center">
                    <span class="text-gray-600 font-medium min-w-[200px]">Plan:</span>
                    <span class="text-[#00423D] font-semibold ml-2">
                      {{ (subscriptionDetails.plan && subscriptionDetails.term) ? `${subscriptionDetails.plan} (${subscriptionDetails.term})` : (subscriptionDetails.plan || subscriptionDetails.term || '-') }}
                    </span>
                    <span 
                      v-if="!isSubscriptionExpired && subscriptionDetails.subscription_status"
                      :class="`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-3 ${
                        subscriptionDetails.subscription_status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : subscriptionDetails.subscription_status === 'inactive'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`"
                    >
                      {{ subscriptionDetails.subscription_status }}
                    </span>
                  </div>

                  <!-- Start Date -->
                  <div class="flex items-center">
                    <span class="text-gray-600 font-medium min-w-[200px]">Start Date:</span>
                    <span class="text-[#00423D] font-semibold ml-2">
                      {{ subscriptionDetails.subscription_start_date ? formatDate(subscriptionDetails.subscription_start_date) : '-' }}
                    </span>
                  </div>

                  <!-- End Date - Only show when subscription has expired -->
                  <div v-if="isSubscriptionExpired" class="flex items-center">
                    <span class="text-gray-600 font-medium min-w-[200px]">End Date:</span>
                    <span class="text-[#00423D] font-semibold ml-2">
                      {{ subscriptionDetails.subscription_end_date ? formatDate(subscriptionDetails.subscription_end_date) : '-' }}
                    </span>
                  </div>
                </div>

                <div v-else class="text-center py-8">
                  <p class="text-gray-500">No subscription details available</p>
                </div>
              </div>

              <div v-if="subscriptionDetails && subscriptionDetails.subscription_status !== 'inactive'" class="w-full md:w-1/5 flex justify-center md:justify-end items-end mt-4 md:mt-0 px-2">
                <button
                  v-if="isSubscriptionExpired"
                  @click="handleRenewClick"
                  class="w-full h-[48px] md:h-[48px] bg-[#3E02A0] text-white font-semibold text-[12px] md:text-[16px] leading-[16px] md:leading-[24px] rounded-[12px] px-2 md:px-6 py-2 md:py-3 flex items-center justify-center"
                >
                  <span class="hidden md:inline">Renew Subscription</span>
                  <span class="md:hidden">Renew</span>
                </button>
                <button
                  v-else
                  @click="cancelSubscription"
                  class="w-full h-[48px] md:h-[48px] bg-[#00423D] text-white font-semibold text-[12px] md:text-[14px] leading-[16px] md:leading-[20px] rounded-[12px] px-2 md:px-6 py-2 md:py-3 flex items-center justify-center"
                >
                  <span class="hidden md:inline">Cancel Subscription</span>
                  <span class="md:hidden">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="line-separate"></div>

        <!-- Change Password Section -->
        <div class="flex flex-col py-5 my-10 lg:py-6 lg:px-6">
          <div>
            <p class="text-lg lg:text-2xl font-bold text-[#00423D] capitalize">
              Change Password:
            </p>

            <div class="flex flex-wrap align-bottom items-center md:items-end lg:items-end mt-5">
              <div class="w-full md:w-4/5">
                <div class="flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="oldPassword"
                      label="Current Password"
                      type="password"
                      placeholder="Enter current password"
                      required
                    />
                  </div>
                </div>

                <div class="flex flex-wrap flex-col lg:flex-row md:flex-row xs:flex-col">
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="newPassword"
                      label="New Password"
                      type="password"
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  <div class="mt-3 lg:flex-1 md:flex-1 px-2">
                    <FormInput
                      v-model="confirmPassword"
                      label="Confirm New Password"
                      type="password"
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/5 flex justify-center md:justify-end mt-4 md:mt-0 px-2">
                <button
                  :class="`${
                    disabledChangePasswordButton
                      ? 'bg-stone-300 w-full h-[48px] md:h-[48px] text-white font-semibold text-[12px] md:text-[16px] leading-[16px] md:leading-[24px] py-2 md:py-3 px-2 md:px-6 rounded-[12px] flex items-center justify-center'
                      : 'w-full h-[48px] md:h-[48px] bg-[#00423D] text-white font-semibold text-[12px] md:text-[16px] leading-[16px] md:leading-[24px] rounded-[12px] px-2 md:px-6 py-2 md:py-3 flex items-center justify-center'
                  }`"
                  :disabled="disabledChangePasswordButton"
                  @click="updatePassword"
                >
                  <span class="hidden md:inline">Change Password</span>
                  <span class="md:hidden">Change</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="line-separate"></div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useVuelidate } from "@vuelidate/core";
import { required, url, email, helpers } from "@vuelidate/validators";
import FormInput from "@/components/FormInput.vue";
import FormPhoneInput from "@/components/FormPhoneInput.vue";
import FormLogoUpload from "@/components/FormLogoUpload.vue";
import FormEmailInput from "@/components/FormEmailInput.vue";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";
import defaultLogo from "@/assets/upload_logo.svg";
import ButtonAni from "@/assets/loading-button-simple.json";
import { Vue3Lottie } from "vue3-lottie";
import Swal from "sweetalert2";

const router = useRouter();
const authStore = useAuthStore();
const submitBtn = ref(null);
const personalSubmitBtn = ref(null);
const isSendingRequest = ref(false);
const isSendingPersonalRequest = ref(false);
const buttonAnimation = ref(ButtonAni);

const fieldTouched = ref({
  phone: false,
});

const personalFieldTouched = ref({
  phone: false,
});

const userInformation = ref({
  firstName: "",
  lastName: "",
  email: "",
  position: "",
  phone: "",
  image: "",
});

const personalForm = ref({
  name: "",
  position: "",
  phone: "",
  image: null,
  phoneCountry: "us",
  isPhoneValid: false,
  phoneValidationObj: null,
  email: "",
  preferred_language: "",
  time_zone: "",
});

const form = ref({
  name: "",
  directorName: "",
  address: "",
  phone: "",
  website: "",
  year: "",
  logo: null,
  phoneCountry: "us",
  isPhoneValid: false,
  phoneValidationObj: null,
});

const profileImageDisplay = computed(() => {
  if (typeof personalForm.value.image === 'string' && (personalForm.value.image.startsWith('http') || personalForm.value.image.startsWith('/'))) {
    return personalForm.value.image;
  }
  return personalForm.value.image ? personalForm.value.image : defaultLogo;
});

const isPersonalFormChanged = computed(() => {
  return (
    personalForm.value.name !== userInformation.value.name ||
    personalForm.value.position !== userInformation.value.position ||
    personalForm.value.phone !== userInformation.value.phone ||
    personalForm.value.image !== userInformation.value.image
  );
});

const alertIsVisible = ref(false);
const alertType = ref("");
const alertMessage = ref("");

const oldPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

const disabledChangePasswordButton = ref(true);

const userRole = ref("");

// Subscription details reactive data
const subscriptionDetails = ref(null);
const subscriptionLoading = ref(false);

const isValidPhoneNumber = (value) => {
  if (!value) return false;
  return form.value.isPhoneValid;
};

const isValidPersonalPhoneNumber = (value) => {
  if (!value) return false;
  return personalForm.value.isPhoneValid;
};

const rules = computed(() => ({
  form: {
    name: { required: helpers.withMessage("Organization Name is required", required) },
    directorName: { required: helpers.withMessage("Director Name is required", required) },
    address: { required: helpers.withMessage("Organization Address is required", required) },
    phone: {
      required: helpers.withMessage("Phone number is required", required),
      validPhoneNumber: helpers.withMessage("Please enter a valid phone number for the selected country", isValidPhoneNumber),
    },
    website: {
      required: helpers.withMessage("Website URL is required", required),
      url: helpers.withMessage("Must be a valid URL address!", helpers.regex(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9]+([\-.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,5}(:[0-9]{1,5})?(\/.*)?$/)),
    },
    year: { required: helpers.withMessage("Year is required", required) },
    logo: { required: helpers.withMessage("Organization Logo is required", required) },
  },
  personalForm: {
    name: { 
      required: helpers.withMessage("Full name is required", required),
      format: helpers.withMessage("Please enter both first and last name", (value) => {
        return value && value.trim().split(' ').length >= 2;
      })
    },
    position: { required: helpers.withMessage("Position is required", required) },
    phone: {
      required: helpers.withMessage("Phone number is required", required),
      validPhoneNumber: helpers.withMessage(
        "Please enter a valid phone number for the selected country", 
        isValidPersonalPhoneNumber
      ),
      format: helpers.withMessage("Phone number must be in a valid format", (value) => {
        if (!value) return true;
        return /^\+?\d{7,15}$/.test(value.replace(/[^\d+]/g, ''));
      })
    },
    image: { required: helpers.withMessage("Profile picture is required", required) },
    email: { 
      required: helpers.withMessage("Email is required", required),
      email: helpers.withMessage("Invalid email format", email)
    },
  }
}));

const v$ = useVuelidate(rules, { form, personalForm });

const logoPreview = ref(defaultLogo);
const shouldShowError = (field) => {
  return (fieldTouched.value[field.$model] || v$.value.$errors.length > 0) && field.$error;
};

const handlePhoneValidation = (validation) => {
  form.value.isPhoneValid = validation.valid;
  form.value.phoneValidationObj = validation;
  v$.value.form.phone.$touch();
};

const handleCountryChange = (country) => {
  form.value.phoneCountry = country.iso2;
  v$.value.form.phone.$touch();
};

const logoDisplay = computed(() => {
  return form.value.logo ? form.value.logo : logoPreview.value;
});

const handlePersonalPhoneValidation = (validation) => {
  personalForm.value.isPhoneValid = validation.valid;
  personalForm.value.phoneValidationObj = validation;

  if (validation.valid) {
    personalForm.value.phone = formatPhoneNumber(personalForm.value.phone, validation);
  }

  if (personalFieldTouched.value.phone) {
    v$.value.personalForm.phone.$touch();
  }
};

const handlePersonalCountryChange = (country) => {
  personalForm.value.phoneCountry = country.iso2;
  if (personalForm.value.phone && personalFieldTouched.value.phone) {
    v$.value.personalForm.phone.$touch();
  }
};

const formatPhoneNumber = (phone, validation) => {
  if (!phone || !validation) return '';
  
  let formattedNumber = phone.replace(/[^\d+]/g, '');
  
  if (validation.countryCode && !formattedNumber.startsWith('+')) {
    formattedNumber = '+' + formattedNumber;
  }
  
  return formattedNumber;
};

async function showAlert(type, message) {
  alertType.value = `alert-${type}`;
  alertMessage.value = message;
  alertIsVisible.value = true;
  setTimeout(() => {
    alertIsVisible.value = false;
  }, 3000);
}

function closeAlert() {
  alertIsVisible.value = false;
}

function formatYear(event) {
  form.value.year = event.target.value.replace(/[^0-9]/g, "");
}

const mapFormToApiData = () => {
  const formData = new FormData();
  
  formData.append('name', form.value.name);
  formData.append('director_name', form.value.directorName);
  formData.append('address', form.value.address);
  formData.append('phone', form.value.phone);
  formData.append('website', form.value.website);
  formData.append('year', form.value.year);
  
  if (form.value.logo instanceof File) {
    formData.append('logo', form.value.logo);
  } else if (typeof form.value.logo === 'string' && form.value.logo.startsWith('data:image')) {
    const blob = dataURLtoBlob(form.value.logo);
    formData.append('logo', blob, 'logo.png');
  }
  
  return formData;
};

const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
};

const mapApiDataToForm = (apiData) => {
  form.value.name = apiData.name || "";
  form.value.directorName = apiData.director_name || "";
  form.value.address = apiData.address || "";
  form.value.phone = apiData.phone || "";
  form.value.website = apiData.website || "";
  form.value.year = apiData.year || "";
  
  if (apiData.logo_url) {
    logoPreview.value = apiData.logo_url;
    form.value.logo = apiData.logo_url;
  }
};

const submitForm = async () => {
  v$.value.form.$touch();
  
  if (!v$.value.form.$error) {
    isSendingRequest.value = true;
    if (submitBtn.value) submitBtn.value.disabled = true;
    
    try {
      const formData = mapFormToApiData();
      
      const res = await fetch(`${global.API_URL}/organizations`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`
        },
        body: formData
      });
      
      if (res.ok) {
        showAlert("success", "Organization information has been updated successfully");
        await fetchOrganizationData();
      } else {
        const errorData = await res.json();
        showAlert("error", errorData.message || "Failed to update organization information");
      }
    } catch (err) {
      console.error("API Error:", err);
      showAlert("error", "An error occurred while updating organization information");
    } finally {
      isSendingRequest.value = false;
      if (submitBtn.value) submitBtn.value.disabled = false;
    }
  }
};

const updatePersonalInfo = async () => {
  v$.value.personalForm.$touch();
  
  if (!v$.value.personalForm.$error) {
    isSendingPersonalRequest.value = true;
    if (personalSubmitBtn.value) personalSubmitBtn.value.disabled = true;
    
    try {
      const formData = new FormData();
      
      const nameParts = personalForm.value.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      formData.append('first_name', firstName);
      formData.append('last_name', lastName);
      formData.append('position', personalForm.value.position);
      
      if (personalForm.value.phone && personalForm.value.phoneValidationObj) {
        const formattedPhone = formatPhoneNumber(personalForm.value.phone, personalForm.value.phoneValidationObj);
        formData.append('phone', formattedPhone);
      } else {
        formData.append('phone', personalForm.value.phone || '');
      }
      
      formData.append('preferred_language', personalForm.value.preferred_language);
      formData.append('time_zone', personalForm.value.time_zone);
      
      if (personalForm.value.image instanceof File) {
        formData.append('logo', personalForm.value.image);
      } else if (typeof personalForm.value.image === 'string' && personalForm.value.image.startsWith('data:image')) {
        const blob = dataURLtoBlob(personalForm.value.image);
        formData.append('logo', blob, 'logo.png');
      }
      
      const res = await fetch(`${global.API_URL}/me`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`
        },
        body: formData
      });
      
      if (res.ok) {
        showAlert("success", "Personal information has been updated successfully");
        await fetchUserData();
      } else {
        const errorData = await res.json();
        showAlert("error", errorData.message || "Failed to update personal information");
      }
    } catch (err) {
      console.error("API Error:", err);
      showAlert("error", "An error occurred while updating personal information");
    } finally {
      isSendingPersonalRequest.value = false;
      if (personalSubmitBtn.value) personalSubmitBtn.value.disabled = false;
    }
  }
};

const fetchUserData = async () => {
  try {
    const res = await fetch(`${global.API_URL}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    
    if (res.ok) {
      const response = await res.json();
      const data = response.data || response; // Handle both response formats
      
      // Update userInformation for the greeting
      userInformation.value = {
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        position: data.position || "",
        phone: data.phone || "",
        image: data.logo_url || data.logo || "",
        name: `${data.first_name || ""} ${data.last_name || ""}`.trim(),
      };

      // Store the user role (only if not already set from subscription API)
      if (!userRole.value) {
        userRole.value = data.role || "";
      }

      // Update personalForm with all fields
      personalForm.value = {
        name: `${data.first_name || ""} ${data.last_name || ""}`.trim(),
        position: data.position || "",
        phone: data.phone || "",
        email: data.email || "",
        image: data.logo_url || data.logo || null,
        phoneCountry: "us",
        isPhoneValid: true,
        phoneValidationObj: null,
        preferred_language: data.preferred_language || "",
        time_zone: data.time_zone || "",
      };

      // If there's a phone number, validate it
      if (data.phone) {
        personalForm.value.isPhoneValid = true;
        personalFieldTouched.value.phone = true;
      }

      // If there's an image, update the preview
      if (data.logo_url || data.logo) {
        personalForm.value.image = data.logo_url || data.logo;
      }

      console.log("Fetched user data:", data); // Debug log
      console.log("Updated personal form:", personalForm.value); // Debug log
    } else {
      const errorData = await res.json();
      console.error("API Error Response:", errorData); // Debug log
      showAlert("error", errorData.message || "Failed to fetch user information");
    }
  } catch (err) {
    console.error("Error fetching user data:", err);
    showAlert("error", "An error occurred while fetching user information");
  }
};

const fetchOrganizationData = async () => {
  try {
    const res = await fetch(`${global.API_URL}/organizations`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json"
      }
    });
    
    if (res.ok) {
      const data = await res.json();
      if (data && data.data) {
        mapApiDataToForm(data.data);
      }
    } else {
      const errorData = await res.json();
      showAlert("error", errorData.message || "Failed to fetch organization information");
    }
  } catch (err) {
    console.error("Error fetching organization data:", err);
    showAlert("error", "An error occurred while fetching organization information");
  }
};

async function updatePassword() {
  const data = {
    current_password: oldPassword.value,
    password: newPassword.value,
    password_confirmation: confirmPassword.value,
  };

  try {
    const res = await fetch(`${global.API_URL}/password`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (res.status == 200) {
      showAlert("success", "Password has been updated successfully");
      // Clear password fields
      oldPassword.value = "";
      newPassword.value = "";
      confirmPassword.value = "";
    } else {
      showAlert("error", "Something is wrong");
    }
  } catch (err) {
    // console.log(err);
  }
}

// Update watch for password fields to match reference code
watch(
  [oldPassword, newPassword, confirmPassword],
  ([oldPassword, newPassword, confirmNewPassword]) => {
    // Check if the conditions for enabling the change password button are met
    if (
      oldPassword !== "" &&
      newPassword !== "" &&
      confirmNewPassword !== "" &&
      confirmNewPassword == newPassword
    ) {
      disabledChangePasswordButton.value = false;
    } else {
      disabledChangePasswordButton.value = true;
    }
  }
);

// Add a computed property to check if the user is an owner
const isOwner = computed(() => userRole.value === "owner");

// Add computed property to check if subscription has expired
const isSubscriptionExpired = computed(() => {
  if (!subscriptionDetails.value || !subscriptionDetails.value.subscription_end_date) {
    return false;
  }
  
  const endDate = new Date(subscriptionDetails.value.subscription_end_date);
  const currentDate = new Date();
  
  return endDate < currentDate;
});

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return '-';
  }
};

// Fetch subscription details
const fetchSubscriptionDetails = async () => {
  subscriptionLoading.value = true;
  try {
    const res = await fetch(`${global.API_URL}/me/subscription_details`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    
    if (res.ok) {
      const response = await res.json();
      // Handle the nested structure where subscription details are under subscription_details key
      const details = response.subscription_details || response.data || response;
      
      // Check if we have meaningful subscription data
      if (details && (details.plan || details.term || details.subscription_status || details.subscription_start_date)) {
        subscriptionDetails.value = details;
      } else {
        // No meaningful subscription data found
        subscriptionDetails.value = null;
      }
      
      // Extract user role from current_user_info if available
      if (response.current_user_info && response.current_user_info.role) {
        userRole.value = response.current_user_info.role;
      }
      
    } else {
      const errorData = await res.json();
      console.error("Subscription API Error Response:", errorData);
      subscriptionDetails.value = null;
    }
  } catch (err) {
    console.error("Error fetching subscription details:", err);
    subscriptionDetails.value = null;
  } finally {
    subscriptionLoading.value = false;
  }
};

// Cancel subscription function
const cancelSubscription = async () => {
  // Open email client to info@askafundraiser.com with empty body for user to type their own message
  window.location.href = `mailto:info@askafundraiser.com`;
};

// Renew subscription function
const renewSubscription = async () => {
  // Navigate to renewal payment page
  router.push({ name: 'renewal-payment' });
};

// Handle renew button click based on user role
const handleRenewClick = () => {  
  if (isOwner.value) {
    renewSubscription();
  } else {
    Swal.fire({
      title: '',
      html: '<div class="text-center"><p class="text-gray-700 text-lg">Please contact your organization owner to renew the subscription.</p></div>',
      confirmButtonText: 'Okay',
      confirmButtonColor: '#00423D',
      showCloseButton: false,
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'px-6 py-2 rounded-lg font-medium'
      }
    });
  }
};

// Function to handle hash-based navigation
const handleHashNavigation = () => {
  const hash = window.location.hash;
  if (hash === '#subscription-details') {
    // Wait a bit for the component to be fully rendered
    setTimeout(() => {
      const subscriptionDetailsElement = document.getElementById('subscription-details');
      if (subscriptionDetailsElement) {
        // Calculate the element's position and center it
        const elementRect = subscriptionDetailsElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const elementHeight = elementRect.height;
        const elementTop = subscriptionDetailsElement.offsetTop;
        
        // Calculate the position to center the element in the viewport with more top offset
        const targetScrollTop = elementTop - (windowHeight / 3) + (elementHeight / 2);
        
        window.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }
    }, 100); // Small delay to ensure DOM is ready
  }
};

const scrollToSubscription = () => {
  const subscriptionDetailsElement = document.getElementById('subscription-details');
  if (subscriptionDetailsElement) {
    // Update the URL hash
    window.location.hash = 'subscription-details';
    
    // Calculate the element's position and center it
    const elementRect = subscriptionDetailsElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const elementHeight = elementRect.height;
    const elementTop = subscriptionDetailsElement.offsetTop;
    
    // Calculate the position to center the element in the viewport with more top offset
    const targetScrollTop = elementTop - (windowHeight / 3) + (elementHeight / 2); // Increased offset to show from top side
    
    window.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
  }
};

onMounted(async () => {
  // Fetch subscription details first as it contains user role information
  await fetchSubscriptionDetails();
  await fetchUserData();
  await fetchOrganizationData();
  
  // Handle hash-based navigation after data is loaded
  handleHashNavigation();
});
</script>

<style scoped>
.alert {
  position: relative;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 10px;
}

.alert-success {
  background-color: #00423D;
  color: #fff;
}

.alert-error {
  background-color: #f2dede;
  color: #a94442;
}

.close-button {
  position: absolute;
  top: 0px;
  right: 5px;
  background: none;
  border: none;
  font-size: 28px;
  color: #ffffff;
  cursor: pointer;
}

.close-button:hover {
  color: #ffffff;
}

.line-separate {
  width: 100%;
  display: block;
  height: 3px;
  background-color: #00423D;
}

.button-switch-enter-active,
.button-switch-leave-active {
  transition: opacity 0.2s ease;
}

.button-switch-enter-from,
.button-switch-leave-to {
  opacity: 0;
}
</style>